import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  chartGeometry,
  dayLabel,
  nearestIndex,
  pctOf,
  RANGE,
  readout,
  type SeriesPoint,
  shiftDay,
  TICKS,
  type Tone,
  VIEW_H,
  VIEW_W,
  WEEK,
  xOf,
} from '../lib/radar-trends';
import { compact } from '../lib/time';
import { AdminTrendTable } from './AdminTrendTable';
import { AdminTrendTooltip } from './AdminTrendTooltip';

// The radar's 30-day chart (#6715): a step line (straight for a flow metric such as
// Runs last 24h) over a fill tinted by the card's tone, gridlines and y labels at
// max/mid/min, a dashed marker seven days back, and a crosshair with a readout.
//
// The plot is ONE focusable `role="img"` named for what it shows: a pointer (mouse
// hover, or a finger's tap and horizontal drag — `touch-action: pan-y` leaves vertical
// page scroll alone) moves the crosshair, a keyboard by Left/Right; Escape, blur or a
// mouse leaving hides it.
// Its drawn parts are presentational (`role="img"` children), so the readout is ALSO
// spoken from a polite live region outside the img; `AdminTrendTable` is the full
// text alternative.

const TONE_CLASS: Readonly<Record<Tone, string>> = {
  good: 'admin-trend-chart admin-trend-chart--good',
  bad: 'admin-trend-chart admin-trend-chart--bad',
  flat: 'admin-trend-chart admin-trend-chart--flat',
};

export function AdminTrendChart(props: {
  title: string;
  series: readonly SeriesPoint[];
  flow: boolean;
  inverse: boolean;
  tone: Tone;
  today: string;
}): JSX.Element {
  const geo = createMemo(() => chartGeometry(props.series, props.flow));
  const last = () => props.series.length - 1;
  const [hover, setHover] = createSignal<number | null>(null);
  const hovered = () => {
    const i = hover();
    return i === null ? undefined : props.series[i];
  };

  const onMove = (e: PointerEvent & { currentTarget: HTMLDivElement }): void => {
    const box = e.currentTarget.getBoundingClientRect();
    const rel = box.width > 0 ? (e.clientX - box.left) / box.width : 1;
    setHover(nearestIndex(props.series, RANGE - Math.round(rel * RANGE)));
  };
  const onKey = (e: KeyboardEvent): void => {
    const i = hover() ?? last() + (e.key === 'ArrowLeft' ? 1 : 0);
    if (e.key === 'ArrowLeft') setHover(Math.max(0, i - 1));
    else if (e.key === 'ArrowRight') setHover(Math.min(last(), i + 1));
    else if (e.key === 'Escape') setHover(null);
    else return;
    e.preventDefault();
  };

  return (
    <div class={TONE_CLASS[props.tone]}>
      <div class="admin-trend-chart__yaxis" aria-hidden="true">
        <For each={geo().grid}>
          {(v) => <span style={{ top: pctOf(geo().y(v), VIEW_H) }}>{compact(v)}</span>}
        </For>
      </div>
      <div
        class="admin-trend-chart__plot"
        role="img"
        tabindex="0"
        aria-label={t('admin.trend_chart_label', { title: props.title })}
        onPointerMove={onMove}
        onPointerDown={onMove}
        onPointerLeave={(e) => {
          // A lifted finger "leaves" too: keep a tapped readout until the next tap or blur.
          if (e.pointerType !== 'touch') setHover(null);
        }}
        onPointerCancel={() => setHover(null)}
        onBlur={() => setHover(null)}
        onKeyDown={onKey}
      >
        <For each={geo().grid}>
          {(v) => (
            <div class="admin-trend-chart__grid" style={{ top: pctOf(geo().y(v), VIEW_H) }} />
          )}
        </For>
        <div class="admin-trend-chart__vs" style={{ left: pctOf(xOf(WEEK), VIEW_W) }}>
          <span class="admin-trend-chart__vs-label">
            {t('admin.trend_vs', { date: dayLabel(shiftDay(props.today, -WEEK), props.today) })}
          </span>
        </div>
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="none" aria-hidden="true">
          <path class="admin-trend-chart__area" d={geo().area} />
          <path class="admin-trend-chart__line" d={geo().line} vector-effect="non-scaling-stroke" />
        </svg>
        <Show
          when={hovered()}
          fallback={
            <div
              class="admin-trend-chart__dot admin-trend-chart__dot--end"
              style={{
                left: '100%',
                top: pctOf(geo().y(props.series[last()]?.value ?? 0), VIEW_H),
              }}
            />
          }
        >
          {(p) => (
            <>
              <div class="admin-trend-chart__cross" style={{ left: pctOf(xOf(p().ago), VIEW_W) }} />
              <div
                class="admin-trend-chart__dot admin-trend-chart__dot--hover"
                style={{
                  left: pctOf(xOf(p().ago), VIEW_W),
                  top: pctOf(geo().y(p().value), VIEW_H),
                }}
              />
              <AdminTrendTooltip
                series={props.series}
                index={hover() ?? last()}
                inverse={props.inverse}
                today={props.today}
              />
            </>
          )}
        </Show>
      </div>
      <div class="sr-only" aria-live="polite">
        {hover() === null ? '' : readout(props.series, hover() ?? 0, props.today)}
      </div>
      <div class="admin-trend-chart__xaxis" aria-hidden="true">
        <For each={TICKS}>
          {(ago) => (
            <span
              classList={{
                'admin-trend-chart__tick': true,
                'admin-trend-chart__tick--start': ago === RANGE,
                'admin-trend-chart__tick--end': ago === 0,
              }}
              style={{ left: pctOf(xOf(ago), VIEW_W) }}
            >
              {dayLabel(shiftDay(props.today, -ago), props.today)}
            </span>
          )}
        </For>
      </div>
      <AdminTrendTable title={props.title} series={props.series} today={props.today} />
    </div>
  );
}
